// A login the identity server vouched for that no account owns yet. The server
// keeps the login's verified claims; this tab holds the token it issued for the
// person in the meantime — a real platform token, bound to this browser's key,
// that says the account is pending. It opens the account endpoints and nothing
// else, it is never renewed, and the session a choice opens replaces it.
//
// Two slots, both sessionStorage on purpose — what is here survives the
// hosted-login redirects in the same tab and dies with it:
//
//   - the CURRENT login: the one that just landed on a choice screen (create an
//     account, restore one, connect to the matched one) — its token, its id, and
//     the person's own provider and email for the screen to show them;
//   - the PARKED login: "connect this login to my existing account" — the
//     person signs in again with the login they already use, and once that
//     session stands the parked login is attached and cleared. Only its id is
//     kept: the attach is made by the session, on a pending login this
//     browser's key holds.

const CURRENT_KEY = 'aac.login.pending.v1';
const PARKED_KEY = 'aac.login.pending.parked.v1';

function readCurrent() {
    const raw = sessionStorage.getItem(CURRENT_KEY);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * `id` names the pending login at the identity server (the token's subject);
 * `identity` is `{ provider, email }` — the person's own login, as the identity
 * server named it.
 */
export function setPendingLogin({ accessToken, id, identity = null }) {
    sessionStorage.setItem(CURRENT_KEY, JSON.stringify({ accessToken, id, identity }));
}

export function getPendingLoginToken() {
    return readCurrent()?.accessToken ?? null;
}

export function getPendingLoginIdentity() {
    return readCurrent()?.identity ?? null;
}

export function clearPendingLogin() {
    sessionStorage.removeItem(CURRENT_KEY);
}

/** Moves the current login to the parked slot. Returns false when there was none to park. */
export function parkPendingLogin() {
    const id = readCurrent()?.id;
    if (!id) {
        return false;
    }
    sessionStorage.setItem(PARKED_KEY, id);
    sessionStorage.removeItem(CURRENT_KEY);
    return true;
}

export function peekParkedLogin() {
    return sessionStorage.getItem(PARKED_KEY);
}

export function takeParkedLogin() {
    const id = sessionStorage.getItem(PARKED_KEY);
    sessionStorage.removeItem(PARKED_KEY);
    return id;
}

export const pendingLogin = {
    setPendingLogin,
    getPendingLoginToken,
    getPendingLoginIdentity,
    clearPendingLogin,
    parkPendingLogin,
    peekParkedLogin,
    takeParkedLogin
};
export default pendingLogin;
